import React, { useState, useEffect, useMemo, useCallback } from 'react';
import BalanceRadarChart from '../../components/balanceRadarChart';
import {
    useTheme,
    Box,
    Typography,
    Divider,
    Grid,
    useMediaQuery,
    Tooltip,
} from '@mui/material';
import buildingsBackground from '../../assets/images/fondo-edificiospng.png';
import { publicAxios } from '../../apiClients';
import BackButton from '../../components/BackButton';
import SelectOptionMM from '../../components/SelectOptionMM';
import { useParams } from 'react-router-dom';
import { generateFirmRouteSegments } from '../../utils/slugify';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { RANKING_TOOLTIPS } from '../../components/rankingTooltips';
import RankingsTable from '../../components/RankingsTable';
import Loading from '../../components/Loading';
import logoMMWhite from '../../assets/banners/MM-blanco sin fondo.png';

// Función auxiliar para convertir hex a rgba
const hexToRgba = (hex, alpha) => {
    const hexValue = hex.replace('#', '');
    const r = parseInt(hexValue.substring(0, 2), 16);
    const g = parseInt(hexValue.substring(2, 4), 16);
    const b = parseInt(hexValue.substring(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const MarketComparativeLegal = () => {
    const theme = useTheme();

    // Uso de useMediaQuery para detectar pantallas pequeñas
    const isMobile = useMediaQuery('(max-width:600px)');
    const { countrySlug, firmSlug } = useParams();

    // Estado para la segunda firma seleccionada (sin incluir Firma 1)
    const [selectedFirmId, setSelectedFirmId] = useState('');
    const [firm, setFirm] = useState(null);
    const [allFirms, setAllFirms] = useState([]);
    const [otherFirms, setOtherFirms] = useState([]);
    const [error, setError] = useState(null);
    const [firmId, setFirmId] = useState(null);
    const [resolvedSlugs, setResolvedSlugs] = useState({ countrySlug: null, firmSlug: null });
    const [isResolvingSlug, setIsResolvingSlug] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const [firmRankings, setFirmRankings] = useState([]);
    const [selectedFirmRankings, setSelectedFirmRankings] = useState([]);

    const [comparativeFirmsData, setComparativeFirmsData] = useState([]);
    const [additionalFirmDetails, setAdditionalFirmDetails] = useState({});
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedRegion, setSelectedRegion] = useState('');


    const getFirmId = useCallback((item) => {
        if (!item || typeof item !== 'object') {
            return null;
        }

        return (
            item.id ??
            item.marketMonitorLegalId ??
            item.marketMonitorLegal?.id ??
            item.MarketMonitorLegal?.id ??
            item.contactDetail?.marketMonitorLegalId ??
            null
        );
    }, []);

    const getCountryName = useCallback((item) => {
        return (
            item?.Country?.name ??
            item?.country?.name ??
            item?.countryName ??
            item?.CountryName ??
            ''
        );
    }, []);

    const getRegionName = useCallback((item) => {
        const region =
            item?.Country?.region?.name ??
            item?.country?.region?.name ??
            item?.Country?.region ??
            item?.country?.region ??
            '';

        return typeof region === 'string' ? region : '';
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
        setError(null);
        setFirmId(null);
        setIsResolvingSlug(true);
        setFirm(null);
        setIsLoading(true);

        if (!firmSlug) {
            setError('Firm identifier is missing.');
            setIsResolvingSlug(false);
            setIsLoading(false);
            return;
        }
        const resolveFirmId = async () => {
            try {
                const response = await publicAxios.get('/institution/market-monitor-legal/table');
                const tableData = response.data ?? [];
                setAllFirms(tableData);
                let strictMatch = null;
                let strictSegments = null;
                let looseMatch = null;
                let looseSegments = null;

                tableData.forEach((item) => {
                    const segments = generateFirmRouteSegments({
                        institutionName: item.institutionName,
                        countryName: item.Country?.name ?? item.country?.name,
                        countryId: item.Country?.id ?? item.country?.id,
                        firmId: item.id,
                    });

                    if (
                        countrySlug &&
                        !strictMatch &&
                        segments.firmSlug === firmSlug &&
                        segments.countrySlug === countrySlug
                    ) {
                        strictMatch = item;
                        strictSegments = segments;
                    }

                    if (!looseMatch && segments.firmSlug === firmSlug) {
                        looseMatch = item;
                        looseSegments = segments;
                    }
                });

                const matchedFirm = strictMatch ?? looseMatch;
                const matchedSegments = strictSegments ?? looseSegments;


                if (!matchedFirm) {
                    setError('Unable to find the requested firm.');
                    return;
                }

                if (matchedSegments) {
                    setResolvedSlugs({
                        countrySlug: matchedSegments.countrySlug,
                        firmSlug: matchedSegments.firmSlug,
                    });
                } else {
                    setResolvedSlugs({ countrySlug: null, firmSlug: null });
                }

                setFirmId(matchedFirm.id);
            } catch (fetchError) {
                console.error('Error resolving firm slug:', fetchError);
                setError('Unable to load comparative data.');
            } finally {
                setIsResolvingSlug(false);
            }
        };

        resolveFirmId();
    }, [countrySlug, firmSlug]);

    useEffect(() => {
        if (!firmId) {
            setFirm(null);
            setComparativeFirmsData([]);
            setIsLoading(isResolvingSlug);
            return;
        }

        const fetchFirm = async () => {
            try {
                const response = await publicAxios.get(
                    `/institution/market-monitor-legal/${firmId}`
                );
                const firmData = response.data;
                setFirm(firmData);

                const countryName = getCountryName(firmData);
                setSelectedCountry(countryName || '');

                const segments = generateFirmRouteSegments({
                    institutionName: firmData?.institutionName,
                    countryName: firmData?.Country?.name ?? firmData?.country?.name,
                    countryId: firmData?.Country?.id ?? firmData?.country?.id,
                    firmId,
                });

                setResolvedSlugs({
                    countrySlug: segments.countrySlug,
                    firmSlug: segments.firmSlug,
                });
            } catch (fetchError) {
                console.error('Error fetching firm data:', fetchError);
                setError('Unable to load comparative data.');
            }
        };

        const fetchComparativeFirms = async () => {
            try {
                const response = await publicAxios.get(
                    '/institution/market-monitor-legal/comparative'
                );
                const normalizedFirms = response.data
                    .map((item) => {
                        const normalizedId = getFirmId(item);

                        if (!normalizedId) {
                            return null;
                        }

                        if (item.id === normalizedId) {
                            return item;
                        }

                        return {
                            ...item,
                            id: normalizedId,
                        };
                    })
                    .filter(Boolean);
                setComparativeFirmsData(normalizedFirms);
            } catch (fetchError) {
                console.error('Error fetching comparative firms:', fetchError);
                setError('Unable to load comparative data.');
            }
        };

        const fetchData = async () => {
            setIsLoading(true);
            try {
                await Promise.all([fetchFirm(), fetchComparativeFirms()]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [firmId, getFirmId, getCountryName, isResolvingSlug]);

    useEffect(() => {
        if (!firmId) {
            setOtherFirms([]);
            return;
        }

        const normalizedFirms = allFirms
            .map((item) => {
                const normalizedId = getFirmId(item);

                if (!normalizedId || normalizedId === firmId) {
                    return null;
                }

                if (item.id === normalizedId) {
                    return item;
                }

                return {
                    ...item,
                    id: normalizedId,
                };
            })
            .filter(Boolean);

        setOtherFirms(normalizedFirms);
    }, [allFirms, firmId, getFirmId]);

    useEffect(() => {
        if (!firmId) {
            setFirmRankings([]);
            return;
        }

        let isMounted = true;

        const fetchFirmRankings = async () => {
            try {
                const response = await publicAxios.get(`/ranking/${firmId}`);
                if (isMounted) {
                    setFirmRankings(response.data || []);
                }
            } catch (fetchError) {
                console.error('Error fetching firm rankings:', fetchError);
                if (isMounted) {
                    setFirmRankings([]);
                }
            }
        };

        fetchFirmRankings();

        return () => {
            isMounted = false;
        };
    }, [firmId]);

    useEffect(() => {
        if (!selectedFirmId) {
            setSelectedFirmRankings([]);
            return;
        }

        let isMounted = true;

        const fetchSelectedFirmRankings = async () => {
            try {
                const response = await publicAxios.get(`/ranking/${selectedFirmId}`);
                if (isMounted) {
                    setSelectedFirmRankings(response.data || []);
                }
            } catch (fetchError) {
                console.error('Error fetching selected firm rankings:', fetchError);
                if (isMounted) {
                    setSelectedFirmRankings([]);
                }
            }
        };

        fetchSelectedFirmRankings();

        return () => {
            isMounted = false;
        };
    }, [selectedFirmId]);

    const createMetricsArray = useCallback((item) => {
        if (!item || typeof item !== 'object') {
            return null;
        }

        const metrics = [
            item.size,
            item.brandPower,
            item.trajectory,
            item.arena,
            item.handsOnWork,
            item.reputation,
            item.clients,
            item.totalScore,
        ];

        return metrics.some((metric) => metric !== null && metric !== undefined) ? metrics : null;
    }, []);

    const hasValidMetrics = useCallback(
        (metrics) => Array.isArray(metrics) && metrics.some((metric) => metric !== null && metric !== undefined),
        [],
    );

    const firmMetrics = createMetricsArray(firm);

    const uniqueRegions = useMemo(() => {
        return [...new Set(
            otherFirms
                .map((firmItem) => getRegionName(firmItem))
                .filter((region) => Boolean(region))
        )]
            .sort((a, b) => a.localeCompare(b))
            .map((region) => ({ id: region, name: region }));
    }, [otherFirms, getRegionName]);

    const uniqueCountries = useMemo(() => {
        const source = selectedRegion
            ? otherFirms.filter((firmItem) => getRegionName(firmItem) === selectedRegion)
            : otherFirms;

        return [...new Set(
            source
                .map((firmItem) => getCountryName(firmItem))
                .filter((country) => Boolean(country))
        )]
            .sort((a, b) => a.localeCompare(b))
            .map((country) => ({ id: country, name: country }));
    }, [otherFirms, selectedRegion, getCountryName, getRegionName]);

    const filteredOtherFirms = useMemo(() => {
        return otherFirms.filter((firmItem) => {
            const countryName = getCountryName(firmItem);
            const regionName = getRegionName(firmItem);
            const matchesCountry = !selectedCountry || countryName === selectedCountry;
            const matchesRegion = !selectedRegion || regionName === selectedRegion;
            return matchesCountry && matchesRegion;
        });
    }, [otherFirms, selectedCountry, selectedRegion, getCountryName, getRegionName]);

    const sortedOtherFirms = useMemo(
        () =>
            filteredOtherFirms
                .slice()
                .sort((a, b) => a.institutionName.localeCompare(b.institutionName)),
        [filteredOtherFirms]
    );

    const comparativeMetricsById = useMemo(() => {
        return comparativeFirmsData.reduce((acc, firmItem) => {
            const normalizedId = getFirmId(firmItem);

            if (!normalizedId) {
                return acc;
            }

            acc[normalizedId] = {
                metrics: createMetricsArray(firmItem),
                totalScore: firmItem?.totalScore,
            };

            return acc;
        }, {});
    }, [comparativeFirmsData, getFirmId, createMetricsArray]);

    const firmLookup = useMemo(() => {
        return sortedOtherFirms.reduce((acc, f) => {
             const normalizedId = getFirmId(f);

            if (!normalizedId) {
                return acc;
            }

            const metricsEntry =
                comparativeMetricsById[normalizedId] ?? additionalFirmDetails[normalizedId] ?? null;
            const metrics = metricsEntry?.metrics ?? createMetricsArray(f);

            acc[normalizedId] = {
                id: normalizedId,
                name: f.institutionName,
                totalScore: metricsEntry?.totalScore ?? f.totalScore ?? null,
                country: getCountryName(f) || 'N/A',
                metrics,
            };
            return acc;
        }, {});

    }, [
        sortedOtherFirms,
        getCountryName,
        getFirmId,
        comparativeMetricsById,
        additionalFirmDetails,
        createMetricsArray,
    ]);

    const selectOptions = useMemo(
        () =>
            sortedOtherFirms
                .map((f) => {
                    const normalizedId = getFirmId(f);

                    if (!normalizedId) {
                        return null;
                    }

                    return {
                        id: normalizedId,
                        name: f.institutionName,
                    };
                })
                .filter(Boolean),
        [sortedOtherFirms, getFirmId]
    );

    const selectedFirm = selectedFirmId ? firmLookup[selectedFirmId] : null;

    useEffect(() => {
        if (!selectedFirmId) {
            return;
        }

        const comparativeMetrics = comparativeMetricsById[selectedFirmId]?.metrics;
        const extraMetrics = additionalFirmDetails[selectedFirmId]?.metrics;

        if (hasValidMetrics(comparativeMetrics) || hasValidMetrics(extraMetrics)) {
            return;
        }

        let isMounted = true;

        const fetchSelectedFirmDetails = async () => {
            try {
                const response = await publicAxios.get(`/institution/market-monitor-legal/${selectedFirmId}`);
                if (!isMounted) {
                    return;
                }

                const detailData = response.data;
                setAdditionalFirmDetails((prev) => ({
                    ...prev,
                    [selectedFirmId]: {
                        metrics: createMetricsArray(detailData),
                        totalScore: detailData?.totalScore ?? prev[selectedFirmId]?.totalScore ?? null,
                    },
                }));
            } catch (detailError) {
                console.error('Error fetching selected firm details:', detailError);
                if (isMounted) {
                    setAdditionalFirmDetails((prev) => ({
                        ...prev,
                        [selectedFirmId]: prev[selectedFirmId] ?? { metrics: null, totalScore: null },
                    }));
                }
            }
        };

        fetchSelectedFirmDetails();

        return () => {
            isMounted = false;
        };
    }, [
        selectedFirmId,
        comparativeMetricsById,
        additionalFirmDetails,
        createMetricsArray,
        hasValidMetrics,
    ]);

    useEffect(() => {
        if (selectedFirmId && !firmLookup[selectedFirmId]) {
            setSelectedFirmId('');
            setSelectedFirmRankings([]);
        }
    }, [selectedFirmId, firmLookup]);

    const handleCountryChange = (newCountry) => {
        setSelectedCountry(newCountry || '');
        setSelectedRegion('');
    };

    const handleRegionChange = (newRegion) => {
        setSelectedRegion(newRegion || '');
        setSelectedCountry('');
    };

    const primaryColor = '#F67147';
    const secondaryColor = '#47A6F6'; // Color para la segunda firma

    // Mapa de colores para las firmas
    const createGradientBackground = (baseColor) => (ctx) => {
        const { chartArea, ctx: context } = ctx.chart;
        if (!chartArea) {
            return undefined;
        }

        const gradient = context.createRadialGradient(
            chartArea.width / 2,
            chartArea.height / 2,
            0,
            chartArea.width / 2,
            chartArea.height / 2,
            chartArea.width / 2
        );

        gradient.addColorStop(0, hexToRgba(baseColor, 0.2));
        gradient.addColorStop(0.5, hexToRgba(baseColor, 0.5));
        gradient.addColorStop(1, hexToRgba(baseColor, 0.8));

        return gradient;
    };

    const datasets = [];

    if (firm && hasValidMetrics(firmMetrics)) {
        datasets.push({
            label: firm?.institutionName || 'Firma 1',
            data: firmMetrics,
            fill: true,
            borderColor: primaryColor,
            borderWidth: 1,
            pointRadius: 0,
            pointHoverRadius: 0,
            backgroundColor: createGradientBackground(primaryColor),
        });
    }

    if (selectedFirm && hasValidMetrics(selectedFirm.metrics)) {
        datasets.push({
            label: selectedFirm.name,
            data: selectedFirm.metrics,
            fill: true,
            borderColor: secondaryColor,
            borderWidth: 1,
            pointRadius: 0,
            pointHoverRadius: 0,
            backgroundColor: createGradientBackground(secondaryColor),
        });
    }

    const data = {
        labels: [
            'Size',
            'Visibility',
            'Trajectory',
            'Arena',
            'Hands-on Work',
            'Reputation',
            'Clients',
            'Total',
        ],
        datasets, // Usar el array de datasets actualizado
    };

    const renderRankingWithTooltip = (value) => {
        if (value === 'NRF' || value === 'NRPA') {
            return (
                <>
                    {value}
                    <Tooltip title={RANKING_TOOLTIPS[value]} placement="top" arrow>
                        <ErrorOutlineIcon fontSize="small" sx={{ ml: 0.5 }} color="warning" />
                    </Tooltip>
                </>
            );
        }
        return value || 'N/A';
    };

    if (isLoading) {
        return (
            <Loading
                background="#03263D"
                barTrackColor="rgba(255, 255, 255, 0.45)"
                barColor="#FFFFFF"
                logo={logoMMWhite}
                logoAlt="Market Monitor"
            />
        );
    }

    if (error && !isResolvingSlug) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    const options = {
        maintainAspectRatio: false,
        scales: {
            r: {
                type: 'customRadial',
                min: 0,
                max: 100,
                ticks: {
                    min: 0,
                    beginAtZero: true,
                    stepSize: 20,
                    showLabelBackdrop: false,
                    color: theme.palette.gray4,
                    padding: 10,
                    labelOffset: 5,
                    font: {
                        size: 0,
                        family: theme.typography.fontFamily,
                        lineHeight: 1.5,
                    },
                },
                angleLines: {
                    display: true,
                    color: theme.palette.gray4,
                },
                grid: {
                    color: theme.palette.gray4,
                },
                pointLabels: {
                    color: 'white',
                    font: {
                        size: isMobile ? 9 : 13,
                        family: theme.typography.fontFamily,
                    },
                    callback: function (label) {
                        if (label === 'Individual Performance') {
                            return ['Individual', 'Performance'];
                        }
                        return label;
                    },
                },
            },
        },
        plugins: {
            legend: {
                display: false, // No mostrar leyenda
            },
            tooltip: {
                backgroundColor: theme.palette.common.white,
                titleColor: 'white',
                bodyColor: 'white',
                borderWidth: 1,
                borderColor: 'white',
                titleFont: {
                    size: 12,
                    family: theme.typography.fontFamily,
                },
                bodyFont: {
                    size: 12,
                    family: theme.typography.fontFamily,
                },
            },
        },
    };

    return (
        <Box
            sx={{
                backgroundImage: `url(${buildingsBackground})`,
                backgroundSize: 'cover',
                minHeight: isMobile ? '100vh' : '120vh',
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pb: 27,
                pt: 1,
            }}
        >
            <Grid container justifyContent="start" sx={{ mt: 0, ml: 1, mb: 0 }}>
                <BackButton path={-1} />
            </Grid>
            {/* Select sobre el Box horizontal */}
            <Box sx={{ width: isMobile ? '90%' : '60%', mb: 5 }}>
                <Grid container spacing={isMobile ? 2 : 3}>
                    <Grid item xs={12} md={4}>
                        <SelectOptionMM
                            options={uniqueRegions.length > 0 ? uniqueRegions : [{ id: '', name: 'No options' }]}
                            onSelectionChange={handleRegionChange}
                            name="Filter by Region"
                            width="100%"
                            selectedOption={selectedRegion}
                            mmcolor="white"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <SelectOptionMM
                            options={uniqueCountries.length > 0 ? uniqueCountries : [{ id: '', name: 'No options' }]}
                            onSelectionChange={handleCountryChange}
                            name="Filter by Country"
                            width="100%"
                            selectedOption={selectedCountry}
                            mmcolor="white"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <SelectOptionMM
                            options={selectOptions}
                            onSelectionChange={setSelectedFirmId}
                            name="Choose a firm"
                            width="100%"
                            selectedOption={selectedFirmId}
                            mmcolor="white"
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Caja azul transparente */}
            <Box
                sx={{
                    backgroundColor: 'rgba(3, 38, 61, 0.8)',
                    width: '100%',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 14,
                    ...(isMobile && {
                        flexDirection: 'column',
                        px: 2,
                        py: 4,
                    }),
                }}
            >
                {/* Divider izquierdo */}
                {!isMobile && (
                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                            height: '121%',
                            borderColor: 'white',
                            borderWidth: '1px',
                            transform: 'translateY(-7%)',
                        }}
                    />
                )}

                {/* Puntaje izquierdo */}
                <Box
                    sx={{
                        textAlign: 'left',
                        width: isMobile ? '100%' : '20%',
                        marginLeft: '10px',
                        marginTop: isMobile ? '28px' : 0,
                        ...(isMobile && { order: 2 }),
                    }}
                >
                    <Box sx={{ display: 'flex-start', alignItems: 'center' }}>
                        <Box
                            sx={{
                                width: '22px',
                                height: '22px',
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: '50%',
                                marginRight: '10%',
                                marginBottom: '7%',
                            }}
                        />
                        <Typography variant="h5" color="white">
                            {firm?.institutionName}
                        </Typography>
                    </Box>
                    <Typography fontSize={17} color={theme.palette.gray3}>
                        {firm?.Country?.name ?? firm?.country?.name ?? ''}
                    </Typography>
                    <Typography variant="h3" color={theme.palette.primary.main}>
                        {firm?.totalScore || 'N/A'}
                    </Typography>
                    <Typography color="white">Total Score</Typography>
                </Box>

                {/* Gráfico en el centro */}
                <Box
                    sx={{
                        position: 'relative',
                        borderRadius: '16px',
                        paddingY: isMobile ? '0px' : '2%',
                        height: isMobile ? '100%' : '95%',
                        width: isMobile ? '100%' : '55%',
                        ...(isMobile && {
                            order: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '300px', // Establecer una altura mínima menor en pantallas pequeñas
                        }),
                        ...(!isMobile && {
                            minHeight: '500px',
                        }),
                    }}
                >
                    <BalanceRadarChart data={data} options={options} />
                </Box>

                {/* Puntaje derecho */}
                <Box
                    sx={{
                        textAlign: 'right',
                        width: isMobile ? '100%' : '20%',
                        marginRight: '10px',
                        marginTop: isMobile ? '2%' : 0,
                        marginBottom: isMobile ? '15%' : 0,
                        ...(isMobile && { order: 4 }),
                    }}
                >
                    <Box
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Box
                            sx={{
                                width: '22px',
                                height: '22px',
                                backgroundColor: '#3592D2',
                                borderRadius: '50%',
                                marginBottom: '7%',
                                marginLeft: isMobile ? '95%' : '91%',
                            }}
                        />
                        <Typography variant="h5" color="white">
                            {selectedFirm ? selectedFirm.name : 'Select a firm'}
                        </Typography>
                        <Typography fontSize={17} color={theme.palette.gray3}>
                            {selectedFirm?.country || ''}
                        </Typography>
                        <Typography variant="h3" color="#3592D2">
                            {selectedFirm ? (selectedFirm.totalScore ?? 'N/A') : ''}
                        </Typography>
                        <Typography color="white">Total Score</Typography>
                    </Box>
                </Box>

                {/* Divider derecho */}
                {!isMobile && (
                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                            height: '121%',
                            borderColor: 'white',
                            borderWidth: '1px',
                            transform: 'translateY(-7%)',
                        }}
                    />
                )}
            </Box>
            {(firm || selectedFirm) && (
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 1690,
                        mt: isMobile ? 4 : 6,
                        px: isMobile ? 2 : 6,
                    }}
                >
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" color="white" sx={{ mb: 2 }}>
                                {firm?.institutionName ? `${firm.institutionName} Ranked Areas` : 'No data Yet'}
                            </Typography>
                            {firmRankings.length > 0 ? (
                                <RankingsTable
                                    rankings={firmRankings}
                                    renderValue={renderRankingWithTooltip}
                                    outerBoxSx={{ mt: 0, width: '100%' }}
                                    variant="responsive"
                                />
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 200,
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        borderRadius: 2,
                                    }}
                                >
                                    <Typography color="white">No data Yet</Typography>
                                </Box>
                            )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" color="white" sx={{ mb: 2 }}>
                                {selectedFirm?.name ? `${selectedFirm.name} Ranked Areas` : 'No data Yet'}
                            </Typography>
                            {selectedFirm && selectedFirmRankings.length > 0 ? (
                                <RankingsTable
                                    rankings={selectedFirmRankings}
                                    renderValue={renderRankingWithTooltip}
                                    outerBoxSx={{ mt: 0, width: '100%' }}
                                    variant="responsive"                                    
                                />
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: 200,
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        borderRadius: 2,
                                    }}
                                >
                                    <Typography color="white">No data Yet</Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Box>
    );
};

export default MarketComparativeLegal;
